import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsNote,
  SettingsPageHeader,
  SettingsRow,
} from '../SettingsShell/SettingsPage';
import { SetSelect, SetSwitch } from '../SettingsShell/controls';
import { SetLockedRow } from '../SettingsShell/rows';
import type { InvisData } from '../../settings-invis/types';
import {
  INV_ALWAYS,
  INV_INTEREST_SIGNALS,
  INV_NUMBERS,
  INV_PROGRAMME,
  INV_READY_SIGNALS,
} from '../../invisalign/mockData';
import type { InvSettingsSignal } from '../../invisalign/types';

export interface InvisSettingsViewProps {
  /** Kept for the page's call site; the page now reads the one Invisalign mock. */
  data?: InvisData;
  location: string;
  /** Opens the programme page (amendment 33 §3) on a given step or `replies`. */
  onOpenStep?: ((step: string) => void) | undefined;
}

const ALIGNER_OPTS = [
  { value: 'invisalign', label: 'Invisalign' },
  { value: 'clearcorrect', label: 'ClearCorrect' },
  { value: 'spark', label: 'Spark' },
  { value: 'other', label: 'Other' },
] as const;

const LEVELS = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Blue Diamond / Diamond Plus'] as const;
const LEVEL_OPTS = LEVELS.map((l) => ({ value: l, label: l }));

const CONSULT_OPTS = [{ value: 'aligner-45', label: 'Aligner consult · 45 min · Dr. Carter' }];

/**
 * Settings › Invisalign Coordinator — **rebuilt** (amendment 30 §7, board
 * INV5Settings). Five cards, no reasoning paragraphs. Provider Yes/No hides and
 * shows the level; one set of ten emails (no year-end email); the email editor
 * has the new fields and no appointment fields.
 */
export function InvisSettingsView({ location, onOpenStep }: InvisSettingsViewProps) {
  const n = INV_NUMBERS;
  const [system, setSystem] = useState<string>('invisalign');
  const [isProvider, setIsProvider] = useState(n.isProvider);
  const [level, setLevel] = useState<string>(n.providerLevel);
  const [mentionLevel, setMentionLevel] = useState(true);
  const [consult, setConsult] = useState<string>('aligner-45');
  const [interest, setInterest] = useState<InvSettingsSignal[]>(INV_INTEREST_SIGNALS);
  const [ready, setReady] = useState<InvSettingsSignal[]>(INV_READY_SIGNALS);

  const toggleSignal = (list: InvSettingsSignal[], set: (v: InvSettingsSignal[]) => void, id: string) => {
    set(list.map((s) => (s.id === id ? { ...s, on: !s.on } : s)));
  };

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[900px] flex-1 overflow-y-auto px-[30px] pb-16 pt-6">
      <SettingsPageHeader
        eyebrow="AI Brain"
        titleLead="Invisalign"
        titleAccent="Coordinator"
        sub="Finds patients who have shown interest, gets them ready by email and the Invis-Line, and tells your team when to talk to them."
      />

      <SettingsCards>
        {/* 01 Your practice */}
        <SettingsCard n="01" title="Your practice">
          <SettingsRow label="Aligners you offer" control={<SetSelect value={system} options={[...ALIGNER_OPTS]} onChange={setSystem} ariaLabel="Aligners you offer" />} />
          <SettingsRow
            label="Are you an Invisalign provider?"
            desc={!isProvider ? 'ELVA will say “clear aligners”.' : undefined}
            control={<SetSwitch checked={isProvider} onChange={setIsProvider} label="Invisalign provider" />}
          />
          {isProvider ? (
            <>
              <SettingsRow
                label="Your provider level"
                desc={<span className="flex flex-wrap gap-1.5">{LEVELS.map((l) => <span key={l} className="rounded-full border border-[var(--border-default)] px-2 py-0.5 text-[11px] text-[var(--text-secondary)]">{l}</span>)}</span>}
                control={<SetSelect value={level} options={LEVEL_OPTS} onChange={setLevel} ariaLabel="Provider level" />}
              />
              <SettingsRow label="Mention your level to patients" control={<SetSwitch checked={mentionLevel} onChange={setMentionLevel} label="Mention your level" />} />
            </>
          ) : null}
          <SettingsRow label="Aligner consults" desc="The appointment type and doctor a consult books into." control={<SetSelect value={consult} options={CONSULT_OPTS} onChange={setConsult} ariaLabel="Aligner consult type" />} />
          <SettingsRow
            label="The Invis-Line"
            desc="Answers texts and calls patients start, and sends up to 3 programme texts to patients who said yes. Never calls out."
            last
            control={<span className="flex items-center gap-2 font-[var(--font-mono)] text-[13px] text-[var(--text-primary)]">{n.lineNumber}<span className="rounded-full bg-[#EEF6F2] px-2 py-0.5 text-[11px] font-semibold text-[#1B5F45]">Connected</span></span>}
          />
        </SettingsCard>

        {/* 02 Interest signals */}
        <SettingsCard n="02" title="Interest signals" sub="Who ELVA marks as worth asking. Off signals show how many they would add.">
          {interest.map((s, i) => (
            <SignalRow key={s.id} s={s} last={i === interest.length - 1} onToggle={() => { toggleSignal(interest, setInterest, s.id); }} />
          ))}
          <SettingsNote tone="indigo">
            {n.worthAsking} patients are worth asking today. Always left out: not seen in 18 months · in ortho or aligner treatment · smile profile Generally straight · said no or opted out · asked in the last 6 months.
          </SettingsNote>
        </SettingsCard>

        {/* 03 Ready signals */}
        <SettingsCard n="03" title="Ready signals" sub="What moves a patient onto the Call list.">
          {ready.map((s, i) => (
            <SignalRow key={s.id} s={s} last={i === ready.length - 1} onToggle={() => { toggleSignal(ready, setReady, s.id); }} />
          ))}
          <SettingsNote tone="amber">{n.ready} are ready today — some match more than one.</SettingsNote>
        </SettingsCard>

        {/* 04 The programme */}
        <SettingsCard n="04" id="emails" title="The programme" sub="10 emails and 3 texts. Every email carries the consult booking link.">
          {INV_PROGRAMME.map((m) => (
            <SettingsRow
              key={m.id}
              label={
                <span className="flex items-baseline gap-3">
                  <span className="w-[42px] font-[var(--font-mono)] text-[12px] text-[var(--text-muted)]">Day {m.day}</span>
                  <span className="flex items-center gap-2">
                    <span aria-hidden className={cn('text-[13px]', m.kind === 'text' ? 'text-[var(--brand-indigo)]' : 'text-[var(--text-muted)]')}>{m.kind === 'text' ? '💬' : '✉️'}</span>
                    <span>{m.title}</span>
                  </span>
                </span>
              }
              desc={m.kind === 'text' ? <span className="text-[11px] text-[var(--text-muted)]">Only to patients who said yes to texts · from the Invis-Line</span> : undefined}
              control={<Button variant="secondary" size="sm" onClick={() => { onOpenStep?.(m.id); }}>Edit</Button>}
            />
          ))}
          <SettingsRow
            label={<span className="flex items-center gap-2"><span aria-hidden className="text-[13px] text-[var(--brand-indigo)]">💬</span><span>Invis-Line replies</span></span>}
            desc={<span className="text-[11px] text-[var(--text-muted)]">6 fixed texts the line sends inside a conversation the patient started</span>}
            last
            control={<Button variant="secondary" size="sm" onClick={() => { onOpenStep?.('replies'); }}>Edit</Button>}
          />
          <SettingsNote tone="plain">
            When your team logs a concern that has its own email, that email goes next. With no fresh benefit on file, day 11 sends without the benefit sentence.
          </SettingsNote>
        </SettingsCard>

        {/* 05 Always */}
        <SettingsCard n="05" title="Always">
          {INV_ALWAYS.map((a, i) => (
            <SetLockedRow key={a} label={a} last={i === INV_ALWAYS.length - 1} />
          ))}
        </SettingsCard>
      </SettingsCards>

      <SettingsFooter location={location} />
    </div>
  );
}

function SignalRow({ s, last, onToggle }: { s: InvSettingsSignal; last: boolean; onToggle: () => void }) {
  return (
    <SettingsRow
      label={s.name}
      desc={
        <span className="flex flex-col gap-0.5">
          {s.desc !== '' ? <span>{s.desc}</span> : null}
          <span className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="rounded-full border border-[var(--border-default)] px-2 py-0.5 text-[var(--text-muted)]">{s.source}</span>
            {s.on ? (
              <span className="font-[var(--font-mono)] font-semibold text-[var(--text-secondary)]">{s.count} today</span>
            ) : (
              <span className="font-[var(--font-mono)] font-semibold text-[var(--brand-indigo)]">{s.wouldBeAdded ?? 0} would be added</span>
            )}
          </span>
        </span>
      }
      last={last}
      control={<SetSwitch checked={s.on} onChange={onToggle} label={s.name} />}
    />
  );
}
